import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { format, parse, startOfWeek, getDay, parseISO } from "date-fns";
import { pl } from "date-fns/locale";
import { RefreshCcw, X } from "lucide-react";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import "../styles/StudentCalendar.css"; // 🔁 nowy plik stylów dla studenta
import { escapeHtml, roomDetailLines, roomTooltipHtml } from "../utils/roomDisplay.js";

// ====== Konfiguracja lokalizacji ======
const localizer = dateFnsLocalizer({
  format: (date, formatStr, options) =>
    format(date, formatStr, { ...options, locale: pl }),
  parse: (value, formatStr, backupDate, options) =>
    parse(value, formatStr, backupDate, { ...options, locale: pl }),
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 1, locale: pl }),
  getDay,
  locales: { pl },
});

const messages = {
  today: "Dzisiaj",
  previous: "Poprzedni",
  next: "Następny",
  month: "Miesiąc",
  week: "Tydzień",
  day: "Dzień",
  agenda: "Agenda",
  date: "Data",
  time: "Godzina",
  event: "Zajęcia",
  allDay: "Cały dzień",
  noEventsInRange: "Brak wydarzeń w tym zakresie.",
};

// ====== Kolory modułów ======
const colorPalette = [
  "#3b82f6",
  "#ef4444",
  "#22c55e",
  "#f59e0b",
  "#8b5cf6",
  "#06b6d4",
  "#e11d48",
  "#84cc16",
];
const randomColor = () =>
  "#" + Math.floor(Math.random() * 16777215).toString(16);

// ====== Tooltip ======
const CustomEvent = ({ event }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      const roomBlock = roomTooltipHtml(
        event.room_name,
        event.room_location,
        event.room_comment
      );
      tippy(ref.current, {
        content: `
          <div>
            <strong>${escapeHtml(event.title)}</strong><br/>
            ${escapeHtml(format(event.start, "HH:mm"))} - ${escapeHtml(format(event.end, "HH:mm"))}<br/>
            ${roomBlock}
          </div>
        `,
        allowHTML: true,
        theme: "light-border",
        placement: "top",
      });
    }
  }, [event]);

  return <div ref={ref}>{event.module}</div>;
};

// ====== Modal szczegółów ======
const EventModal = ({ event, onClose }) => {
  if (!event) return null;

  return (
    <div className="calendar-modal-overlay">
      <div className="calendar-modal-content">
        <div className="calendar-modal-header">
          <h2>{event.title}</h2>
          <button className="calendar-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="calendar-modal-body">
          <p>
            <strong>Moduł:</strong> {event.module}
          </p>
          <p>
            <strong>Prowadzący:</strong> {event.lecturer}
          </p>
          {roomDetailLines(
            event.room_name,
            event.room_location,
            event.room_comment
          ).map((line, i) => (
            <p key={i}>
              <strong>{line.label}:</strong> {line.value}
            </p>
          ))}
          <p>
            <strong>Data:</strong> {format(event.start, "dd.MM.yyyy")}
            <br />
            <strong>Godziny:</strong>{" "}
            {format(event.start, "HH:mm")} - {format(event.end, "HH:mm")}
          </p>
          <p>
            <strong>Opis:</strong>
            <br />
            {event.description || "Brak opisu"}
          </p>
        </div>
      </div>
    </div>
  );
};

// ====== Główny komponent ======
export default function StudentCalendar() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // stan kolorów modułów
  const [moduleColors, setModuleColors] = useState(() => {
    const saved = localStorage.getItem("studentModuleColors");
    return saved ? JSON.parse(saved) : {};
  });

  // kontrola widoku i daty (jak u wykładowcy)
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState("month"); // 🔁 start: miesiąc

  const token = localStorage.getItem("token");

  // ====== Nadawanie kolorów modułom ======
  const assignColors = (sessions) => {
    setModuleColors((prev) => {
      const updated = { ...prev };
      sessions.forEach((s) => {
        if (!updated[s.module]) {
          const newColor =
            colorPalette[Object.keys(updated).length % colorPalette.length] ||
            randomColor();
          updated[s.module] = newColor;
        }
      });
      localStorage.setItem("studentModuleColors", JSON.stringify(updated));
      return updated;
    });
  };

  // ====== Styl wydarzenia ======
  const eventStyleGetter = (event) => {
    const backgroundColor = moduleColors[event.module] || "#6b7280";
    return {
      style: {
        backgroundColor,
        color: "white",
        borderRadius: "8px",
        border: "none",
        padding: "2px 4px",
      },
    };
  };

  // ====== Pobieranie danych z API ======
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/student/dashboard/upcomingCourses`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = res.data.map((row) => ({
        id: row.session_id,
        title: row.title || "Zajęcia",
        start: parseISO(`${row.session_date}T${row.start_time}`),
        end: parseISO(`${row.session_date}T${row.end_time}`),
        module: row.module_name,
        description: row.description,
        room_name: row.room_name,
        room_location: row.location,
        room_comment: row.room_comment,
        lecturer: row.lecturer || "---",
      }));

      setEvents(data);
      assignColors(data);
    } catch (err) {
      console.error("❌ Błąd pobierania kalendarza studenta:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  if (loading) return <div>Ładowanie kalendarza...</div>;

  return (
    <div className="student-calendar-wrapper">
      <div className="student-calendar-header-row">
        <div>
          <h2 className="student-calendar-title">Kalendarz zajęć</h2>
          <p className="student-calendar-subtitle">
            Kalendarz zajęć i wydarzeń kursanta.
          </p>
        </div>
        <button className="student-calendar-refresh-btn" onClick={fetchEvents}>
          <RefreshCcw size={14} />
          <span>Odśwież</span>
        </button>
      </div>

      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        views={["month", "week", "day"]}
        messages={messages}
        components={{ event: CustomEvent }}
        eventPropGetter={eventStyleGetter}
        onSelectEvent={(event) => setSelectedEvent(event)}
        // kontrola widoku / daty
        view={currentView}
        date={currentDate}
        onView={(view) => setCurrentView(view)}
        onNavigate={(date) => setCurrentDate(date)}
        defaultView="month"
        defaultDate={new Date()}
        // 🔁 start dnia od 7:00 (tydzień / dzień)
        min={new Date(1970, 0, 1, 7, 0, 0)}
        max={new Date(1970, 0, 1, 21, 0, 0)}
        style={{ height: "100%" }}
      />

      {/* Modal szczegółów */}
      {selectedEvent && (
        <EventModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </div>
  );
}
